import axios from 'axios'
import { Message } from 'element-ui'
import { clearSession, hasSession, markSession } from '@/utils/auth'
import { adminCsrfHeaders } from '@/utils/csrf'
import config from '@/config'
import router from '@/router'

async function wipeAuthState () {
  clearSession()
  try {
    const mod = await import('@/store')
    mod.default.commit('auth/CLEAR_SESSION')
  } catch (_) { /* ignore */ }
}

const service = axios.create({
  baseURL: config.baseURL,
  timeout: config.timeout,
  withCredentials: true
})

let refreshPromise = null

function isAuthPath (url) {
  const u = String(url || '')
  return u.includes('/admin/auth/login') ||
    u.includes('/admin/auth/logout') ||
    u.includes('/admin/auth/refresh')
}

async function refreshAdminSession () {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${config.baseURL}/admin/auth/refresh`,
        {},
        {
          withCredentials: true,
          timeout: config.timeout,
          headers: adminCsrfHeaders()
        }
      )
      .then((res) => {
        const body = res && res.data
        if (body && body.code !== undefined && body.code !== 0 && body.code !== 200) {
          throw new Error(body.message || '刷新登录失败')
        }
        markSession()
        return true
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

service.interceptors.request.use(
  (cfg) => {
    cfg.withCredentials = true
    // axios@1 AxiosHeaders：不能整体替换成普通对象展开，否则防伪头可能丢
    const csrf = adminCsrfHeaders()
    if (!cfg.headers) cfg.headers = {}
    if (typeof cfg.headers.set === 'function') {
      Object.keys(csrf).forEach((k) => cfg.headers.set(k, csrf[k]))
    } else {
      Object.keys(csrf).forEach((k) => { cfg.headers[k] = csrf[k] })
    }
    return cfg
  },
  (error) => Promise.reject(error)
)

service.interceptors.response.use(
  (response) => {
    const res = response.data
    if (res && res.code !== undefined && res.code !== 0 && res.code !== 200) {
      const silentError = Boolean(response.config && response.config.silentError)
      if (!silentError) Message.error(res.message || '请求失败')
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return res
  },
  async (error) => {
    const status = error.response?.status
    const cfg = error.config || {}
    const silentError = Boolean(cfg.silentError)

    if (status === 401 && !cfg.__authRetried && !isAuthPath(cfg.url) && hasSession()) {
      try {
        await refreshAdminSession()
        cfg.__authRetried = true
        cfg.withCredentials = true
        return service.request(cfg)
      } catch (_) {
        await wipeAuthState()
        Message.error('登录已过期，请重新登录')
        if (router.currentRoute.path !== '/login') {
          router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } }).catch(() => {})
        }
        return Promise.reject(error)
      }
    }

    if (status === 401) {
      await wipeAuthState()
      if (!silentError) Message.error('登录已过期，请重新登录')
    } else if (status === 403) {
      if (!silentError) Message.error(error.response?.data?.message || '无权限')
    } else if (!silentError) {
      if (status && status >= 500) {
        Message.error(error.response?.data?.message || '服务器错误')
      } else if (error.response) {
        Message.error(error.response?.data?.message || error.message || '请求失败')
      }
    }
    return Promise.reject(error)
  }
)

export default service
